// 手術モバイル page (/m/a-surgery)
// - 主要 KPI 4 件: 手術件数 / 予定 / 緊急 / 全麻 (当月)
// - 月次推移 sparkline (病院全体、 24ヶ月)
// - 診療科 TOP 5 (期間累計、 手術件数)
// - drawer で期間切替 (直近 12 / 24 ヶ月)

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useMeta } from "../lib/mobileApi";
import { MobileShell } from "../components/MobileShell";
import { StatPill, sentimentFromDelta, SENTIMENT_BG, SENTIMENT_RING } from "../components/StatBadge";
import { Sparkline } from "../components/Sparkline";
import { fmtInt } from "../../lib/format";
import type { SurgeryResponse } from "@shared/api";

const FEATURED = ["手術件数", "予定", "緊急", "全麻"];

export function SurgeryMobile() {
  const { data: meta } = useMeta();
  const [months, setMonths] = useState<12 | 24>(24);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // asOfMonth state (= periodTo). meta 読込後に dateMax で初期化、 ←→ で月送り可能。
  const [asOfMonth, setAsOfMonth] = useState<string | null>(null);

  if (!meta) return <div className="p-6 text-sm text-slate-400">読み込み中…</div>;
  const periodTo = asOfMonth ?? meta.dateMax;
  const periodFrom = monthShift(periodTo, -(months - 1));
  const canPrev = monthShift(periodTo, -1).slice(0, 7) >= meta.dateMin.slice(0, 7);
  const canNext = monthShift(periodTo, +1).slice(0, 7) <= meta.dateMax.slice(0, 7);

  return (
    <MobileShell
      title="🔪 手術 速報"
      prototypeId="A"
      prototypeName="手術"
      asOfMonth={periodTo}
      onPrevMonth={() => setAsOfMonth(monthShift(periodTo, -1))}
      onNextMonth={() => setAsOfMonth(monthShift(periodTo, +1))}
      canPrevMonth={canPrev}
      canNextMonth={canNext}
      headerRight={
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="text-slate-500 hover:text-slate-800 active:bg-slate-100 rounded p-1.5 text-base leading-none"
          title="期間切替"
          aria-label="フィルタ"
        >⚙</button>
      }
    >
      <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
        <span className="bg-slate-100 px-1.5 py-0.5 rounded">直近 {months} ヶ月</span>
      </div>

      <SurgeryKpis periodFrom={periodFrom} periodTo={periodTo} asOfMonth={periodTo} />
      <SurgeryRanking periodFrom={periodFrom} periodTo={periodTo} />

      <p className="text-[10px] text-slate-400 text-center px-4 leading-relaxed">
        詳細 (予定/緊急構成・heatmap) は PC 版へ。
      </p>

      {drawerOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={() => setDrawerOpen(false)}>
          <aside
            className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-white shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-800">⚙ フィルタ</h2>
              <button onClick={() => setDrawerOpen(false)} className="text-slate-400 text-xl">×</button>
            </header>
            <div className="px-4 py-3 space-y-4 text-sm flex-1">
              <div>
                <div className="text-xs font-medium text-slate-700 mb-1.5">期間</div>
                <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded p-1">
                  {[12, 24].map((m) => (
                    <button
                      key={m}
                      onClick={() => setMonths(m as 12 | 24)}
                      className={`text-xs py-1.5 rounded ${months === m ? "bg-white font-semibold shadow-sm" : "text-slate-500"}`}
                    >直近 {m} ヶ月</button>
                  ))}
                </div>
              </div>
            </div>
            <footer className="px-4 py-3 border-t border-slate-200">
              <button
                onClick={() => setDrawerOpen(false)}
                className="w-full text-xs py-2 bg-slate-900 text-white rounded font-semibold"
              >適用して閉じる</button>
            </footer>
          </aside>
        </div>
      )}
    </MobileShell>
  );
}

function SurgeryKpis({ periodFrom, periodTo, asOfMonth }: {
  periodFrom: string; periodTo: string; asOfMonth: string;
}) {
  const { data, isLoading } = useQuery<SurgeryResponse>({
    queryKey: ["surgery-mobile", periodFrom, periodTo],
    queryFn: () => api.surgery({ periodFrom, periodTo }),
    placeholderData: (prev) => prev,
  });

  // 当月 (asOfMonth) の集計区分別合計
  const current = useMemo(() => {
    if (!data) return new Map<string, { actual: number; budget?: number; py?: number }>();
    const ymTarget = asOfMonth.slice(0, 7);
    const m = new Map<string, { actual: number; budget?: number; py?: number }>();
    for (const r of data.rows) {
      if (r.date.slice(0, 7) !== ymTarget) continue;
      const k = r.集計区分;
      if (!m.has(k)) m.set(k, { actual: 0 });
      const e = m.get(k)!;
      e.actual += r.実績;
      if (r.予算 != null) e.budget = (e.budget ?? 0) + r.予算;
      if (r.前年実績 != null) e.py = (e.py ?? 0) + r.前年実績;
    }
    return m;
  }, [data, asOfMonth]);

  // 月別合計 (病院全体 = 全 dept 合算、 各集計区分の trend)
  const monthlyTrend = useMemo(() => {
    if (!data) return new Map<string, Map<string, number>>();
    const m = new Map<string, Map<string, number>>();
    for (const r of data.rows) {
      const ym = r.date.slice(0, 7);
      if (!m.has(r.集計区分)) m.set(r.集計区分, new Map());
      const mm = m.get(r.集計区分)!;
      mm.set(ym, (mm.get(ym) ?? 0) + r.実績);
    }
    return m;
  }, [data]);

  const trendArrayFor = (metric: string): (number | null)[] => {
    const mm = monthlyTrend.get(metric);
    if (!mm) return [];
    return Array.from(mm.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([, v]) => v);
  };

  return (
    <section>
      <div className="text-xs text-slate-500 mb-1">
        {asOfMonth.slice(0, 4)}年{asOfMonth.slice(5, 7)}月 主要指標
        {isLoading && <span className="ml-2 text-[9px] text-slate-400">loading…</span>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {FEATURED.map((metric) => {
          const c = current.get(metric);
          const actual = c?.actual ?? 0;
          const budget = c?.budget;
          const py = c?.py;
          const yoyDelta = py != null && py !== 0 ? (actual - py) / Math.abs(py) : null;
          const yoySent = sentimentFromDelta(yoyDelta, "higher_is_better");
          const budgetRatio = budget != null && budget !== 0 ? actual / budget : null;
          const sent = budgetRatio == null ? "neutral"
                      : budgetRatio >= 1.0 ? "positive"
                      : budgetRatio >= 0.95 ? "neutral"
                      : budgetRatio >= 0.9 ? "warning" : "danger";
          const trend = trendArrayFor(metric);
          return (
            <div key={metric} className={`p-2.5 rounded-lg ring-1 ${SENTIMENT_RING[sent]} ${SENTIMENT_BG[sent]}`}>
              <div className="text-[11px] text-slate-600 font-medium">{metric}</div>
              <div className="text-[9px] text-slate-400">件</div>
              <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{fmtInt(actual)}</div>
              {trend.length > 1 && (
                <div className="mt-0.5 -mx-1">
                  <Sparkline values={trend} width={140} height={20} showLast />
                </div>
              )}
              <div className="mt-1 flex flex-wrap gap-1">
                {budgetRatio != null && (
                  <StatPill label="予" value={`${(budgetRatio * 100).toFixed(0)}%`} sentiment={sent} size="xs" />
                )}
                {yoyDelta != null && (
                  <StatPill label="年" value={`${yoyDelta >= 0 ? "+" : ""}${(yoyDelta * 100).toFixed(1)}%`} sentiment={yoySent} size="xs" />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SurgeryRanking({ periodFrom, periodTo }: {
  periodFrom: string; periodTo: string;
}) {
  const { data } = useQuery<SurgeryResponse>({
    queryKey: ["surgery-mobile", periodFrom, periodTo],
    queryFn: () => api.surgery({ periodFrom, periodTo }),
    placeholderData: (prev) => prev,
  });

  // 診療科別 手術件数 期間累計
  const ranking = useMemo(() => {
    if (!data) return [];
    const m = new Map<string, number>();
    for (const r of data.rows) {
      if (r.集計区分 !== "手術件数") continue;
      if (r["診療科"].includes("合計") || r["診療科"] === "総合計") continue;
      m.set(r["診療科"], (m.get(r["診療科"]) ?? 0) + r.実績);
    }
    return Array.from(m.entries())
      .map(([dept, v]) => ({ dept, v }))
      .sort((a, b) => b.v - a.v)
      .slice(0, 10);
  }, [data]);

  return (
    <section>
      <h2 className="text-xs font-semibold text-slate-700 mb-1">診療科別 手術件数 TOP 10 (期間累計)</h2>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {ranking.map((r, i) => (
          <div key={r.dept} className="flex items-center gap-2 px-3 py-1.5 text-xs border-b border-slate-100 last:border-b-0">
            <span className="text-[10px] font-mono text-slate-400 w-4 shrink-0">#{i + 1}</span>
            <span className="text-slate-800 flex-1 truncate">{r.dept}</span>
            <span className="text-slate-700 tabular-nums font-semibold">{fmtInt(r.v)} 件</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function monthShift(d: string, delta: number): string {
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + delta, day));
  return [dt.getUTCFullYear(), String(dt.getUTCMonth() + 1).padStart(2, "0"), String(dt.getUTCDate()).padStart(2, "0")].join("-");
}
